import { Dietary } from '@hungryroot/constants';
import { tags } from '@hungryroot/tags';
import { EatingHabit, type TagId } from './quiz';

/**
 * Broad food-type classification for each avoidance chip. Data-driven diet
 * gating (see `isAvoidanceChipEligible`) keys off this instead of a pile of
 * tag-id literals, so the predicate stays usable for both display filtering
 * and state pruning.
 */
export type AvoidanceCategory =
  | 'meat'
  | 'poultry'
  | 'seafood'
  | 'plant'
  | 'dairy'
  | 'egg'
  | 'other';

export type AvoidanceSectionHeading =
  | 'Major Allergens'
  | 'Proteins'
  | 'Other Ingredients';

interface AvoidanceChipBase {
  label: string;
  section: AvoidanceSectionHeading;
  category: AvoidanceCategory;
}

/** Persists to `quiz.dietary` (a Dietary slug enum full-replace on submit). */
interface DietaryAvoidanceChip extends AvoidanceChipBase {
  kind: 'dietary';
  dietary: Dietary;
}

/** Persists to `quiz.dislike` (a subset-scoped replace on submit). */
interface TagAvoidanceChip extends AvoidanceChipBase {
  kind: 'tag';
  tagId: TagId;
}

export type AvoidanceChip = DietaryAvoidanceChip | TagAvoidanceChip;

// Labels are approved Figma copy — hardcoded, not sourced from the API.
// Order matches the Figma render order within each section.
export const AVOIDANCE_CHIPS: readonly AvoidanceChip[] = [
  // Major Allergens
  {
    kind: 'dietary',
    label: 'Gluten',
    section: 'Major Allergens',
    category: 'other',
    dietary: Dietary.GlutenFree,
  },
  {
    kind: 'dietary',
    label: 'Dairy',
    section: 'Major Allergens',
    category: 'dairy',
    dietary: Dietary.DairyFree,
  },
  {
    kind: 'dietary',
    label: 'Shellfish',
    section: 'Major Allergens',
    category: 'seafood',
    dietary: Dietary.ShellfishFree,
  },
  {
    kind: 'dietary',
    label: 'Soy',
    section: 'Major Allergens',
    category: 'other',
    dietary: Dietary.SoyFree,
  },
  {
    kind: 'dietary',
    label: 'Peanuts',
    section: 'Major Allergens',
    category: 'other',
    dietary: Dietary.PeanutFree,
  },
  {
    kind: 'dietary',
    label: 'Tree nuts',
    section: 'Major Allergens',
    category: 'other',
    dietary: Dietary.TreeNutFree,
  },
  {
    kind: 'dietary',
    label: 'Eggs',
    section: 'Major Allergens',
    category: 'egg',
    dietary: Dietary.EggFree,
  },
  {
    kind: 'dietary',
    label: 'Sesame',
    section: 'Major Allergens',
    category: 'other',
    dietary: Dietary.SesameFree,
  },

  // Proteins
  {
    kind: 'tag',
    label: 'Pork',
    section: 'Proteins',
    category: 'meat',
    tagId: tags.Pork,
  },
  {
    kind: 'tag',
    label: 'Bison',
    section: 'Proteins',
    category: 'meat',
    tagId: tags.Bison,
  },
  {
    kind: 'tag',
    label: 'Lamb',
    section: 'Proteins',
    category: 'meat',
    tagId: tags.Lamb,
  },
  {
    kind: 'tag',
    label: 'Turkey',
    section: 'Proteins',
    category: 'poultry',
    tagId: tags.Turkey,
  },
  // Chicken has no artboard in the Figma for this screen — its inclusion
  // here is a deliberate product decision, not a design omission to be
  // "fixed" back out. Grouped with Turkey as the two poultry chips.
  {
    kind: 'tag',
    label: 'Chicken',
    section: 'Proteins',
    category: 'poultry',
    tagId: tags.Chicken,
  },
  {
    kind: 'tag',
    label: 'Ground beef',
    section: 'Proteins',
    category: 'meat',
    tagId: tags.GroundBeef,
  },
  {
    kind: 'tag',
    label: 'Steak',
    section: 'Proteins',
    category: 'meat',
    tagId: tags.Steak,
  },
  {
    kind: 'tag',
    label: 'Salmon',
    section: 'Proteins',
    category: 'seafood',
    tagId: tags.Salmon,
  },
  {
    kind: 'tag',
    label: 'Smoked salmon',
    section: 'Proteins',
    category: 'seafood',
    tagId: tags.SmokedFish,
  },
  {
    kind: 'tag',
    label: 'Tuna',
    section: 'Proteins',
    category: 'seafood',
    tagId: tags.Tuna,
  },
  {
    kind: 'tag',
    label: 'White fish',
    section: 'Proteins',
    category: 'seafood',
    tagId: tags.Whitefish,
  },
  {
    kind: 'tag',
    label: 'Trout',
    section: 'Proteins',
    category: 'seafood',
    tagId: tags.Trout,
  },
  {
    kind: 'tag',
    label: 'Beans + lentils',
    section: 'Proteins',
    category: 'plant',
    tagId: tags.BeansAndLentils,
  },
  {
    kind: 'tag',
    label: 'Tofu + tempeh',
    section: 'Proteins',
    category: 'plant',
    tagId: tags.TofuAndTempeh,
  },
  {
    kind: 'tag',
    label: 'Meat alternatives',
    section: 'Proteins',
    category: 'plant',
    tagId: tags.MeatAlternatives,
  },

  // Other Ingredients — mixes both persistence kinds; the section heading
  // does NOT drive persistence. Render order matches the Figma.
  {
    kind: 'dietary',
    label: 'Caffeine',
    section: 'Other Ingredients',
    category: 'other',
    dietary: Dietary.CaffeineFree,
  },
  {
    kind: 'dietary',
    label: 'Onion',
    section: 'Other Ingredients',
    category: 'other',
    dietary: Dietary.OnionFree,
  },
  {
    kind: 'dietary',
    label: 'Garlic',
    section: 'Other Ingredients',
    category: 'other',
    dietary: Dietary.GarlicFree,
  },
  {
    kind: 'dietary',
    label: 'Grains',
    section: 'Other Ingredients',
    category: 'other',
    dietary: Dietary.GrainFree,
  },
  {
    kind: 'tag',
    label: 'Paneer',
    section: 'Other Ingredients',
    category: 'dairy',
    tagId: tags.Paneer,
  },
  {
    kind: 'tag',
    label: 'Beets',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Beets,
  },
  {
    kind: 'tag',
    label: 'Eggplants',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Eggplants,
  },
  {
    kind: 'tag',
    label: 'Mushrooms',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Mushrooms,
  },
  {
    kind: 'tag',
    label: 'Chili peppers',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.ChiliPeppers,
  },
  {
    kind: 'tag',
    label: 'Masala spices',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.MasalaSpices,
  },
  {
    kind: 'tag',
    label: 'Miso',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Miso,
  },
  {
    kind: 'tag',
    label: 'Coconut',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Coconut,
  },
  {
    kind: 'tag',
    label: 'Brussel sprouts',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.BrusselSprouts,
  },
  {
    kind: 'tag',
    label: 'Cilantro',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Cilantro,
  },
  {
    kind: 'tag',
    label: 'Bell peppers',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.BellPeppers,
  },
  {
    kind: 'tag',
    label: 'Tomatoes',
    section: 'Other Ingredients',
    category: 'other',
    tagId: tags.Tomatoes,
  },
] as const;

export function isTagAvoidanceChip(
  chip: AvoidanceChip
): chip is TagAvoidanceChip {
  return chip.kind === 'tag';
}

export function isDietaryAvoidanceChip(
  chip: AvoidanceChip
): chip is DietaryAvoidanceChip {
  return chip.kind === 'dietary';
}

/** The full universe of tag ids offered across all avoidance chips. */
export const AVOIDANCE_TAG_UNIVERSE: readonly TagId[] = AVOIDANCE_CHIPS.filter(
  isTagAvoidanceChip
).map((chip) => chip.tagId);

const HIDDEN_CATEGORIES_BY_HABIT: Record<
  EatingHabit,
  readonly AvoidanceCategory[]
> = {
  [EatingHabit.Vegan]: ['meat', 'poultry', 'seafood', 'dairy', 'egg'],
  [EatingHabit.Vegetarian]: ['meat', 'poultry', 'seafood'],
  [EatingHabit.Pescatarian]: ['meat', 'poultry'],
  [EatingHabit.Omnivore]: [],
  [EatingHabit.Flexitarian]: [],
};

/**
 * Shared diet-eligibility predicate. Used both to filter which chips are
 * displayed on the avoidance screens and to prune stale state on a diet
 * change (see `avoidanceMutations.ts`) — the same rule must decide both, or
 * displayed and persisted state can silently drift apart.
 */
export function isCategoryEligibleForHabit({
  category,
  habits,
}: {
  category: AvoidanceCategory;
  habits: EatingHabit | null;
}): boolean {
  if (habits === null) return true;

  return !HIDDEN_CATEGORIES_BY_HABIT[habits].includes(category);
}

export function isAvoidanceChipEligible({
  chip,
  habits,
}: {
  chip: AvoidanceChip;
  habits: EatingHabit | null;
}): boolean {
  return isCategoryEligibleForHabit({ category: chip.category, habits });
}

/**
 * Section-level eligibility for the `meats`/`seafood` positive-rating
 * collections. Every tag returned by the meat/seafood tag-group APIs
 * belongs to one of these sections, whether or not it also has an
 * avoidance chip (e.g. BeefAndBison is `meat` category but has no chip) — so
 * section-level gating, not the avoidance-chip category map, is the
 * source that can't miss a tag. Shared by ProteinPreferences.tsx (section
 * visibility) and `pruneAvoidancesForHabitChange` (diet-change pruning) so
 * displayed and persisted state can't drift apart.
 */
export function getProteinSectionVisibility(habits: EatingHabit | null): {
  meats: boolean;
  seafood: boolean;
} {
  return {
    meats: isCategoryEligibleForHabit({ category: 'meat', habits }),
    seafood: isCategoryEligibleForHabit({ category: 'seafood', habits }),
  };
}
